import React, {useEffect, useMemo, useState} from "react";
import {
  Badge,
  Box, CircularProgress, IconButton, InputAdornment,
  List,
  ListItem, ListItemIcon,
  ListItemText,
  Stack, TextField, Tooltip,
  Typography
} from "@mui/material";
import {grey} from "@mui/material/colors";
import InfiniteScroll from "react-infinite-scroll-component";

import {ConversationRepository} from "../../../../repositories/ConversationRepository";
import {SupervisionChatListItem} from "./supervision-chat-list-item";
import {HugeiconsIcon} from "@hugeicons/react";
import {FilterIcon, Search01Icon} from "@hugeicons/core-free-icons";
import {useDebounce} from "../../../../hooks/useDebounce";
import {FilterModal} from "./filter-modal";
import moment from "moment";
import {useStompWebsocket} from "../../../../hooks/useStompWebsocket";

export const SupervisionChatList = React.memo(({project}) => {
  const [updatedValue] = useStompWebsocket(project?.id ?`/topic/flow/${project?.id}` : null);
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState({page: 0, size: 25, totalElements: 0});
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [openFilter, setOpenFilter] = useState(false);
  const [filters, setFilters] = useState({
    id: "",
    start: null,
    end: null,
    caller: "",
    status: null,
    vdn: "",
  });
  const debouncedFilter = useDebounce(filters, 500);
  const debouncedFetchConversations = useDebounce(updatedValue || null, 2500)

  const getStartOfDay = (date) => moment(date).startOf("day").toDate().getTime(); // 00:00:00
  const getEndOfDay = (date) => moment(date).endOf("day").toDate().getTime();

  const fetchConversations = (append = false, signal) => {
    setLoading(true);
    const filter = {...debouncedFilter};
    if (filter.tagIn) {
      delete filter.tagIn
    }
    if (filter.tagNotIn) {
      delete filter.tagNotIn
    }
    const type = project?.settings?.projectType || "VOICE_BOT"
    ConversationRepository.getConversations({
      flow: project.id,
      page: append ? pagination?.page || 0 : 0,
      size: pagination?.size || 10,
      type: type,
      ...filter,
      start: debouncedFilter?.start ? getStartOfDay(debouncedFilter.start) : undefined,
      end: debouncedFilter?.end ? getEndOfDay(debouncedFilter.end) : undefined,
      tagIds: debouncedFilter?.tagIn?.map((tag) => tag?.id) || [],
      notTagIds: debouncedFilter?.tagNotIn?.map((tag) => tag?.id) || [],
    }, signal)
      .then((response) => {
        const newItems = response?.data?.content || [];
        const total = response?.data?.totalElements || 0;

        setConversations((prev) => {
          if (!append) return newItems;

          const updatedMap = new Map(prev.map(item => [item.id, item]));

          newItems.forEach(item => {
            updatedMap.set(item.id, item); // This updates if exists, or adds if not
          });

          return Array.from(updatedMap.values());
        });
        setPagination((prev) => ({
          ...prev,
          totalElements: total,
        }));
        setHasMore((append ? conversations.length + newItems.length : newItems.length) < total);
        setLoading(false);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          console.log('Request aborted');
        } else {
          console.error(error);
        }
        setLoading(false);
      });
  };

  const params = new URLSearchParams(window.location.search);

  useEffect(() => {
    if (params.toString()) {
      setFilters(prevFilters => {
        const updatedFilters = {...prevFilters};

        // Iterate over all params and set them if key exists in filters
        params.forEach((value, key) => {
          if (key in updatedFilters) {
            updatedFilters[key] = value;
          }
        });

        return updatedFilters;
      });
    }
  }, [params.size]);

  // Initial + periodic refresh
  useEffect(() => {
    if (!project?.id) return;

    const abortController = new AbortController();
    setPagination((prev) => ({...prev, page: 0}));
    fetchConversations(false, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [project?.id, debouncedFilter, debouncedFetchConversations]);

  // Append new page when pagination.page increases
  useEffect(() => {
    if (pagination.page > 0) {
      const abortController = new AbortController();
      fetchConversations(true, abortController.signal);

      return () => {
        abortController.abort();
      };
    }
  }, [pagination.page]);

  const onUpdateConversation = (conv) => {
    setConversations(prev => prev?.map(i => i?.id === conv?.id ? {...conv} : {...i}))
  }

  const loadMoreData = () => {
    if (!loading && hasMore) {
      console.log("load more")
      setPagination((prev) => ({...prev, page: prev.page + 1}));
    }
  };

  const [activeCalls, userRefuseToSpeakConv, recentCalls] = useMemo(() => {
    const activeConv = conversations.filter(i => i?.status === "ACTIVE");
    const userRefuseToSpeakConv = conversations.filter(i => (i?.status === "USER_NOT_TALKING" || i?.status === "MISSED_CALL") && !i?.resolved);
    const recent = conversations.filter(i => i?.status !== "ACTIVE" && i?.status !== "USER_NOT_TALKING" && i?.status !== "MISSED_CALL" || i?.resolved)
    return [activeConv, userRefuseToSpeakConv, recent];
  }, [conversations]);

  // Single ticking clock for all active call durations (instead of one interval per list item)
  const hasActiveCalls = activeCalls.length > 0;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!hasActiveCalls) return;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [hasActiveCalls]);


  const filterCount = useMemo(() => {
    return Object.values(filters)?.filter(i => !!i)?.length;
  }, [filters])

  return (
    <>
      <FilterModal
        filters={filters} setFilters={setFilters}
        handleClose={() => setOpenFilter(false)}
        open={openFilter} project={project} />
      <Box sx={{px: 2, pb: 2}}>
        <Stack direction={"row"} gap={1} sx={{pb: 2}} justifyContent={"space-between"} alignItems={"center"}>
          <Typography fontWeight={600} fontSize={"1.1em"}>Conversation Viewer</Typography>
          <Tooltip title={"Filter Conversations"}>
            <IconButton size={"small"} onClick={() => setOpenFilter(true)}>
              <Badge badgeContent={filterCount} color={"error"} size={"small"}
                     sx={{fontSize: "0.85em"}}>
                    <HugeiconsIcon icon={FilterIcon} size={22}/>
              </Badge>
            </IconButton>
          </Tooltip>
        </Stack>

        <TextField
          size={"small"} placeholder={"Search Conversation"} fullWidth
          sx={{borderRadius: 2}}
          value={filters?.caller}
          onChange={(e) => setFilters(prev => ({...prev, caller: e?.target?.value}))}
          InputProps={{
            sx: {borderRadius: 2},
            startAdornment: (
              <InputAdornment position={"start"}>{loading ? <CircularProgress size={20}/> :
                <HugeiconsIcon icon={Search01Icon} size={20}/>}</InputAdornment>
            )
          }}
        />
      </Box>
      <Stack
        id="scrollableList"
        sx={{flex: 1, overflowY: "auto", pl: 0.5}}
      >

        {/* Active Calls */}
        {activeCalls?.length > 0 && (
          <List>
            <ListItem
              sx={{
                py: 0,
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: "background.paper",
              }}
            >
              <ListItemText
                primary="Active Calls"
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: grey[600],
                }}
              />
            </ListItem>
            {activeCalls.map((conversation) => (
              <SupervisionChatListItem
                key={conversation?.id}
                conversation={conversation}
                project={project}
                now={now}
                onUpdateConversation={onUpdateConversation}
              />
            ))}
          </List>
        )}
        {/* Potentional problem Calls */}
        {userRefuseToSpeakConv?.length > 0 && (
          <List>
            <ListItem
              sx={{
                py: 0,
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: "background.paper",
              }}
            >
              <ListItemText
                primary="No meaningful user input"
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: grey[600],
                }}
              />
            </ListItem>
            {userRefuseToSpeakConv.map((conversation) => (
              <SupervisionChatListItem
                key={conversation?.id}
                conversation={conversation}
                project={project}
                onUpdateConversation={onUpdateConversation}
              />
            ))}
          </List>
        )}

        {/* Recent Calls (infinite scroll) */}
        <List>
          <ListItem
            sx={{
              py: 0, pb: 1,
              position: "sticky",
              top: 0,
              zIndex: 1,
              bgcolor: "background.paper",
            }}
          >
            <ListItemText
              primary={filterCount ? "Filtered Results": "Recent Calls"}
              primaryTypographyProps={{
                fontWeight: 600,
                color: grey[600],
              }}
            />
            {
              filterCount ?
                <ListItemIcon sx={{fontSize: "0.8em", pr: 0, align: "right"}}>
                  Total Conversations: {pagination?.totalElements}
                </ListItemIcon> :null
            }
          </ListItem>

          <InfiniteScroll
            dataLength={conversations.length}
            next={loadMoreData}
            hasMore={hasMore}
            loader={
              <Box p={2} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Loading more...
                </Typography>
              </Box>
            }
            scrollableTarget="scrollableList"
          >
            {recentCalls.map((conversation) => (
              <SupervisionChatListItem
                key={conversation?.id}
                conversation={conversation}
                project={project}
                onUpdateConversation={onUpdateConversation}
              />
            ))}
          </InfiniteScroll>
        </List>
      </Stack>
    </>
  );
});
